/* eslint-disable @typescript-eslint/no-explicit-any */
import { concat, flow, isEmpty, map, omit, sortBy } from 'es-toolkit/compat';
import { oc } from 'ts-optchain';

import * as Yup from 'yup';
import { assertNever, filterNilValues } from './typeUtils';
import { core, safeEnumCompare } from '@tock/coretypes';
import { logger } from '@tock/logging';
import protos from 'utils/protos';

const { TicketTypeQuestion } = protos;

export const DEFAULT_PROMPT_VALUE = 'Provide any additional notes';
export const OTHER_OPTION_VALUE = 'Other';
export const TEXT_CHARACTER_LIMIT = 1000;
export const TEXT_CHARACTER_LIMIT_WARNING = 'Answer cannot exceed 1,000 characters';

export type AnsweredQuestion = core.PurchaseQuestion & {
  formattedResponse: { response: string; comment: string };
};

export interface QuestionTextResponse {
  value: string;
}

export interface QuestionCheckboxResponse {
  value: string[];
  otherValue: string;
}

export interface QuestionRadioResponse {
  value: string;
  otherValue: string;
}

export interface QuestionSelectResponse {
  value: string;
  otherValue: string;
}

export interface QuestionMultiSelectResponse {
  value: string[];
  otherValue: string;
}

export type QuestionResponse =
  | QuestionTextResponse
  | QuestionCheckboxResponse
  | QuestionRadioResponse
  | QuestionSelectResponse
  | QuestionMultiSelectResponse;

type FormattedQuestionResponse = {
  response: string;
  comment: string;
};

export type QuestionFieldName = `question-${string}`;

export type PreVisitQuestionsFields = Record<
  QuestionFieldName,
  QuestionTextResponse | QuestionRadioResponse | QuestionCheckboxResponse
>;

export const getQuestionFieldName = (question: core.ITicketTypeQuestion): QuestionFieldName => {
  return `question-${question.id}`;
};

export function convertPurchaseQuestionsToFields(
  questions: core.PurchaseQuestion[]
): PreVisitQuestionsFields {
  const entries = questions.map((question) => {
    switch (question.response) {
      case 'textResponse':
        return [
          getQuestionFieldName(question),
          {
            value: question.textResponse?.value ?? '',
          },
        ];
      case 'radioResponse': {
        const value = question.radioResponse?.value ?? '';
        const otherValue = question.radioResponse?.otherValue ?? '';

        if (!question.allowOther) {
          return [
            getQuestionFieldName(question),
            {
              value: value,
              otherValue: otherValue,
            },
          ];
        }

        return [
          getQuestionFieldName(question),
          {
            value: otherValue ? OTHER_OPTION_VALUE : value,
            otherValue: otherValue,
          },
        ];
      }
      case 'checkboxResponse': {
        let values = (question.checkboxResponse?.values ?? [])
          .map((value) => value.value)
          .filter(filterNilValues);
        const otherValue = question.checkboxResponse?.otherValue ?? '';

        if (!question.allowOther) {
          return [
            getQuestionFieldName(question),
            {
              value: values,
              otherValue: otherValue,
            },
          ];
        }

        if (otherValue) {
          values = values.filter((value) => value !== otherValue);
          return [
            getQuestionFieldName(question),
            {
              value: [...values, OTHER_OPTION_VALUE],
              otherValue: otherValue,
            },
          ];
        }
        return [
          getQuestionFieldName(question),
          {
            value: values,
            otherValue: '',
          },
        ];
      }
      default: {
        return [
          getQuestionFieldName(question),
          {
            value: '',
            otherValue: '',
          },
        ];
      }
    }
  });
  return Object.fromEntries(entries);
}

export const getDefaultFieldValue = (type: core.TicketTypeQuestion['type']): QuestionResponse => {
  switch (type) {
    case 'TEXTAREA':
    case 'TEXTINPUT':
      return { value: '' };

    case 'RADIO_BUTTON':
      return { value: '', otherValue: '' };

    case 'CHECKBOXES':
      return { value: [], otherValue: '' };
  }
};

export function getOtherTicketTypeQuestionOption(
  question: core.TicketTypeQuestion
): core.TicketTypeQuestionOption {
  return {
    id: 0,
    name: OTHER_OPTION_VALUE,
    text: OTHER_OPTION_VALUE,
    ordering: question.options.length,
  };
}

function convertToAdditionalTextQuestion(
  question: core.TicketTypeQuestion
): core.TicketTypeQuestion {
  if (question.allowOther) {
    return {
      ...question,
      allowOther: false,
      additionalTextPrompt: DEFAULT_PROMPT_VALUE,
      options: [...question.options, getOtherTicketTypeQuestionOption(question)],
    };
  }

  return question;
}

export function convertToAdditionalTextQuestions(
  questions: core.TicketTypeQuestion[]
): core.TicketTypeQuestion[] {
  return questions.map(convertToAdditionalTextQuestion);
}

export function convertTicketTypeQuestionsToFields(
  questions: core.TicketTypeQuestion[]
): PreVisitQuestionsFields {
  const entries = questions.map((question) => [
    getQuestionFieldName(question),
    getDefaultFieldValue(question.type),
  ]);
  return Object.fromEntries(entries);
}

export function convertToRadioResponseRemoveOther(
  response: QuestionRadioResponse,
  options: core.IPurchaseQuestionOption[]
): core.IPurchaseQuestionRadioResponse {
  if (!!response.otherValue && response.value === OTHER_OPTION_VALUE) {
    return {
      otherValue: response.otherValue,
    };
  }

  const selectedOption = options.find((option) => option.text === response.value);

  return {
    value: response.value,
    visitTag: selectedOption?.visitTag,
  };
}

export function convertToRadioResponse(
  response: QuestionRadioResponse,
  options: core.IPurchaseQuestionOption[]
): core.IPurchaseQuestionRadioResponse {
  const selectedOption = options.find((option) => option.text === response.value);

  return {
    value: response.value,
    otherValue: response.otherValue,
    visitTag: selectedOption?.visitTag,
  };
}

export function convertToCheckboxResponseRemoveOther(
  response: QuestionCheckboxResponse,
  options: core.IPurchaseQuestionOption[]
): core.IPurchaseQuestionCheckboxResponse {
  const hasOtherValue = response.value?.includes(OTHER_OPTION_VALUE);

  const values = response.value
    .filter((value) => value !== OTHER_OPTION_VALUE)
    .map((value) => {
      const selectedOption = options.find((option) => option.text === value);
      return {
        value: value,
        visitTag: selectedOption?.visitTag,
      };
    });

  return {
    values: values,
    otherValue: hasOtherValue ? response.otherValue : undefined,
  };
}

export function convertToCheckboxResponse(
  response: QuestionCheckboxResponse,
  options: core.IPurchaseQuestionOption[]
): core.IPurchaseQuestionCheckboxResponse {
  const values = response.value.map((value) => {
    const selectedOption = options.find((option) => option.text === value);
    return {
      value: value,
      visitTag: selectedOption?.visitTag,
    };
  });

  return {
    values: values,
    otherValue: values.length > 0 ? response.otherValue : undefined,
  };
}

export function convertToPurchaseQuestions(
  questions: core.PurchaseQuestion[] | core.TicketTypeQuestion[],
  values: PreVisitQuestionsFields
): core.IPurchaseQuestion[] {
  const responses = questions.map((question) => values[getQuestionFieldName(question)]);

  return questions.map((question, idx) => {
    const updatedQuestion = omit(question, 'id');
    const updatedOptions = question.options.map((option) => omit(option, 'id'));

    const response = responses[idx];

    switch (question.type) {
      case 'TEXTAREA':
      case 'TEXTINPUT':
        return {
          ...updatedQuestion,
          /**
           * At the moment, it's possible for a text question to include a list of options
           * if the question was once a radio or checkbox question and included options.
           *
           * So we need to pass along the list of options for text questions.
           */
          options: updatedOptions,
          textResponse: {
            value: (response as QuestionTextResponse).value,
          },
        };
      case 'RADIO_BUTTON':
        return {
          ...updatedQuestion,
          options: updatedOptions,
          radioResponse: question.allowOther
            ? convertToRadioResponseRemoveOther(response as QuestionRadioResponse, updatedOptions)
            : convertToRadioResponse(response as QuestionRadioResponse, updatedOptions),
        };
      case 'CHECKBOXES':
        return {
          ...updatedQuestion,
          options: updatedOptions,
          checkboxResponse: question.allowOther
            ? convertToCheckboxResponseRemoveOther(
                response as QuestionCheckboxResponse,
                updatedOptions
              )
            : convertToCheckboxResponse(response as QuestionCheckboxResponse, updatedOptions),
        };
      default:
        return {
          ...updatedQuestion,
          options: updatedOptions,
        };
    }
  });
}

/**
 * @deprecated cleanup with PRE_VISIT_QUESTION_TAGS flag
 */
export function getResponseStringForQuestion(question: core.PurchaseQuestion): string {
  if (question.response) {
    return getResponseStringForQuestionWithResponse(question);
  } else {
    return getResponseStringForQuestionWithoutResponse(question);
  }
}

const isSingleResponseType = (
  response: core.PurchaseQuestion['response']
): response is 'textResponse' | 'radioResponse' | 'checkboxResponse' => {
  const responseValues = ['textResponse', 'radioResponse', 'checkboxResponse'];
  return responseValues.includes(response ?? '');
};

const isMultiResponseType = (
  response: core.PurchaseQuestion['response']
): response is 'radioResponse' | 'checkboxResponse' => {
  const responseValues = ['radioResponse', 'checkboxResponse'];
  return responseValues.includes(response ?? '');
};

function getResponseStringForQuestionWithResponse(question: core.PurchaseQuestion): string {
  const { response } = question;
  let responseValue;
  if (isSingleResponseType(response)) {
    responseValue = question?.[response]?.value ?? '';
  }
  let responseOtherValue = '';
  if (isMultiResponseType(response)) {
    responseOtherValue = question?.[response]?.otherValue ?? '';
  }
  if (!Array.isArray(responseValue)) {
    const responses = [responseValue, responseOtherValue];
    return responses.filter(Boolean).join(', ');
  }
  let checkboxAnswers = responseValue;
  if (responseOtherValue) {
    checkboxAnswers = concat(checkboxAnswers, [responseOtherValue]);
  }
  return checkboxAnswers.join(', ');
}

function getResponseStringForQuestionWithoutResponse(question: core.PurchaseQuestion): string {
  const textResponseValue = oc(question).textResponse.value();
  const checkboxResponseValue = oc(question).checkboxResponse.value();
  const checkboxResponseOtherValue = oc(question).checkboxResponse.otherValue();
  const radioButtonResponseValue = oc(question).radioResponse.value();
  const radioButtonResponseOtherValue = oc(question).radioResponse.otherValue();
  if (!Array.isArray(checkboxResponseValue)) {
    if (question.type === 'RADIO_BUTTON') {
      const responses = [radioButtonResponseValue, radioButtonResponseOtherValue];
      return responses.filter(Boolean).join(', ');
    }

    return textResponseValue || '';
  } else {
    let checkboxResponse = checkboxResponseValue;
    if (checkboxResponseOtherValue) {
      checkboxResponse = concat(checkboxResponseValue, [checkboxResponseOtherValue]);
    }
    return checkboxResponse.join(', ');
  }
}

export function getOrderedTicketTypeQuestions(
  questions: core.ITicketTypeQuestion[]
): core.TicketTypeQuestion[] {
  return flow(
    (questions: core.ITicketTypeQuestion[]) =>
      map(questions, (question: any) => ({
        ...question,
        options: sortBy(question.options, 'ordering'),
      })),
    (questions: core.ITicketTypeQuestion[]) => sortBy(questions, 'ordering')
  )(questions) as core.TicketTypeQuestion[];
}

export function getOrderedPurchaseQuestions(
  questions: core.IPurchaseQuestion[]
): core.PurchaseQuestion[] {
  return flow(
    (questions: core.IPurchaseQuestion[]) =>
      map(questions, (question: any) => ({
        ...question,
        response: question.response, // non-enumerable property not copied in spread
        options: sortBy(question.options, 'ordering'),
      })),
    (questions: core.IPurchaseQuestion[]) => sortBy(questions, 'ordering')
  )(questions) as core.PurchaseQuestion[];
}

export function formatAnsweredQuestions(questions: any[]): core.PurchaseQuestion[] {
  return map(questions, (question: any) => {
    const { response, options, otherResponse, type } = question;
    const formattedOptions = map(options as any[], (option: any, index: any) => ({
      ...option,
      name: option.text,
      ordering: index,
    }));

    if (safeEnumCompare(type, TicketTypeQuestion.Type, TicketTypeQuestion.Type.TEXTINPUT)) {
      question.textResponse = { value: response };
    } else if (safeEnumCompare(type, TicketTypeQuestion.Type, TicketTypeQuestion.Type.TEXTAREA)) {
      question.textResponse = { value: response };
    } else if (
      safeEnumCompare(type, TicketTypeQuestion.Type, TicketTypeQuestion.Type.RADIO_BUTTON)
    ) {
      question.radioResponse = isEmpty(otherResponse)
        ? { value: response }
        : { otherValue: otherResponse };
    } else if (safeEnumCompare(type, TicketTypeQuestion.Type, TicketTypeQuestion.Type.CHECKBOXES)) {
      question.checkboxResponse = {
        value: response,
        otherValue: otherResponse,
      };
    } else {
      logger.error('Unknown question type');
    }

    return {
      ...question,
      options: formattedOptions,
    };
  });
}

/**
 * Normalizes answers from all question types to a single object containing
 * formatted strings for read-only display.
 *
 * Used within dashboard
 */
export function getFormattedResponseFromQuestion(
  question: core.PurchaseQuestion
): FormattedQuestionResponse {
  switch (question.type) {
    case 'TEXTAREA':
    case 'TEXTINPUT':
      return { response: question.textResponse?.value ?? '', comment: '' };

    case 'RADIO_BUTTON':
      return {
        response: question.radioResponse?.value ?? '',
        comment: question.radioResponse?.otherValue ?? '',
      };

    case 'CHECKBOXES': {
      const selections = question.checkboxResponse?.value ?? [];
      return {
        response: selections.filter(Boolean).join(', '),
        comment: question.checkboxResponse?.otherValue ?? '',
      };
    }

    default:
      assertNever(question.type);
  }
}

export function getOrderedQuestionsWithAnswers(questions: core.IPurchaseQuestion[]) {
  const orderedQuestions = getOrderedPurchaseQuestions(questions);
  const questionsWithAnswers: AnsweredQuestion[] = [];

  orderedQuestions.forEach((question) => {
    const formattedResponse = getFormattedResponseFromQuestion(question);

    if (formattedResponse.response || formattedResponse.comment) {
      questionsWithAnswers.push({ ...question, formattedResponse });
    }
  });

  return questionsWithAnswers;
}

export function getHasAnsweredQuestions(questions: core.IPurchaseQuestion[]) {
  return getOrderedQuestionsWithAnswers(questions).length > 0;
}

/**
 * Combines response and optional comment in to one string for display
 */
export function getStringFromFormattedResponse({ response, comment }: FormattedQuestionResponse) {
  return [response, comment].filter(Boolean).join(', ');
}

function createQuestionValidationSchema(question: core.TicketTypeQuestion | core.PurchaseQuestion) {
  switch (question.type) {
    case 'TEXTAREA':
    case 'TEXTINPUT': {
      return Yup.object({
        value: Yup.string().max(TEXT_CHARACTER_LIMIT, TEXT_CHARACTER_LIMIT_WARNING),
      }).nullable();
    }
    case 'CHECKBOXES': {
      return Yup.object({
        value: Yup.array(),
        otherValue: Yup.string().max(TEXT_CHARACTER_LIMIT, TEXT_CHARACTER_LIMIT_WARNING),
      }).nullable();
    }
    case 'RADIO_BUTTON': {
      return Yup.object({
        value: Yup.string(),
        otherValue: Yup.string().max(TEXT_CHARACTER_LIMIT, TEXT_CHARACTER_LIMIT_WARNING),
      }).nullable();
    }
  }
}

export const createValidationSchema = (
  question: core.TicketTypeQuestion[] | core.PurchaseQuestion[]
) => {
  const entries = question.map((question) => [
    getQuestionFieldName(question),
    createQuestionValidationSchema(question),
  ]);

  return Yup.object(Object.fromEntries(entries));
};

export const isTicketTypeQuestionType = (value: string): value is core.TicketTypeQuestion.Type => {
  return ['TEXTINPUT', 'TEXTAREA', 'RADIO_BUTTON', 'CHECKBOXES'].includes(value);
};
